import {
  calculateAverageDifficulty,
  cloneObject,
  deserify,
  getTeacherSubjectEnum,
} from '@geneo2-web/shared-ui';
import {
  DifficultyLevelEnum,
  Question,
} from '@protos/content_management/content.db_pb';
import { AssessmentContent } from '@protos/learning_management/lms.assessment.common.apis_pb';
import {
  ChapterInfo,
  ChapterIdsInfo,
  MarkingSchemeEnum,
  MultiSubjectSection,
  MultiSubjectSectionStatusEnum,
  MultiSubjectSubSection,
  QuestionModuleMap,
  SectionQuestionMetaData,
  TaskStudentAttemptStatusEnum,
} from '@protos/learning_management/lms.db_pb';
import { useAppDispatch, useAppSelector } from '../../../reduxStore/reduxHooks';
import { setFAContent } from '../reducer/assessmentFlow.slice';
import { Subject_SubjectEnum } from '@protos/common/common.db_pb';
import { TeachClassSubjects } from '@protos/user_management/ums.login.apis_pb';

// =============================================================

/**
 * Returns unique question IDs from all sections of fa_content
 * (main questions and alternate questions in every subSectionMetaData).
 */
export const getUniqueQuestionIdsFromFAContent = (props: {
  faContent: AssessmentContent | undefined;
  sectionNumbersToIgnore?: number[];
}): string[] => {
  const { faContent, sectionNumbersToIgnore } = props;
  if (!faContent?.multiSubjectSections?.length) return [];

  const ignoreSet = sectionNumbersToIgnore?.length
    ? new Set(sectionNumbersToIgnore)
    : undefined;

  const ids = new Set<string>();
  for (const section of faContent.multiSubjectSections) {
    if (ignoreSet?.has(section.sectionNo ?? 0)) continue;

    for (const subSection of section.multiSubjectSubSections ?? []) {
      for (const meta of subSection.subSectionMetaData ?? []) {
        if (meta.questionId) ids.add(meta.questionId);

        for (const alt of meta.alternateQuestions ?? []) {
          if (alt.questionId) ids.add(alt.questionId);
        }
      }
    }
  }
  return Array.from(ids);
};

// =============================================================

export const useAddOrReplaceFAQuestionMultiSubject = () => {
  const { fa_content, fa_selected_section_number } = deserify(
    useAppSelector((state) => state.assessment)
  );
  const assessmentContent = fa_content?.assessmentContent || [];
  const dispatch = useAppDispatch();

  return (props: {
    question: Question;
    subSectionIndex: number;
    questionIndex?: number;
    altIdx?: number;
    moduleMapArray?: QuestionModuleMap[];
  }) => {
    const { question, subSectionIndex, questionIndex, altIdx, moduleMapArray } =
      props;

    const sectionIndex =
      fa_content?.multiSubjectSections?.findIndex(
        (s) => (s.sectionNo ?? 0) === fa_selected_section_number
      ) ?? -1;
    if (sectionIndex < 0) return;
    const section = fa_content?.multiSubjectSections[sectionIndex];
    if (!section) return;
    const subSection = section.multiSubjectSubSections[subSectionIndex];
    if (!subSection) return;

    // 1. Add as new question (append to section when index is past the end)
    if (
      typeof questionIndex !== 'number' ||
      questionIndex === subSection.subSectionMetaData.length
    ) {
      const meta = subSection.subSectionMetaData;
      const maxSequence =
        meta.length > 0 ? Math.max(...meta.map((val) => val.sequence ?? 0)) : 0;
      const questionType = question.questionType;
      const subSectionQuestionTypeMarks = subSection.questionTypeAndMarks;
      const questionTypeMarks = subSectionQuestionTypeMarks.find(
        (val) => val.questionType === questionType
      );
      if (
        questionTypeMarks &&
        question?.question?.model?.value?.commonQuestionContent
      ) {
        question.question.model.value.commonQuestionContent.marks[0] =
          questionTypeMarks.marks;
      }
      const newQuesMeta = new SectionQuestionMetaData({
        questionId: question.questionId || '',
        sequence: maxSequence + 1,
        showMedia: true,
        isAiGenerated: question.isAiGenerated,
        maxMarks:
          question?.question?.model?.value?.commonQuestionContent?.marks[0] ??
          0,
      });
      subSection.subSectionMetaData.push(newQuesMeta);
      section.multiSubjectSubSections[subSectionIndex] = subSection;
      const updatedContent = cloneObject(fa_content);
      updatedContent.multiSubjectSections[sectionIndex] = section;
      const ifNewQuestionExistInOtherSection =
        updatedContent.multiSubjectSections?.some((sec, secIdx) =>
          sec.multiSubjectSubSections?.some((subSec, subSecIdx) =>
            subSec.subSectionMetaData?.some(
              (ques, qIdx) =>
                (secIdx !== sectionIndex || subSecIdx !== subSectionIndex) &&
                (ques.questionId === question.questionId ||
                  (ques.alternateQuestions ?? []).some(
                    (alt) => alt.questionId === question.questionId
                  ))
            )
          )
        );
      console.log({ ifNewQuestionExistInOtherSection });
      if (!ifNewQuestionExistInOtherSection) {
        updatedContent.assessmentContent = [...assessmentContent, question];
        if (updatedContent.questionsSequenceInfo) {
          updatedContent.questionsSequenceInfo.questionModuleMap = [
            ...(updatedContent.questionsSequenceInfo.questionModuleMap ?? []),
            ...(moduleMapArray?.filter(
              (val) => val.questionId === question.questionId
            ) ?? []),
          ];
        }
      }
      dispatch(setFAContent(updatedContent || undefined));
      return;
    }

    const quesMeta = subSection.subSectionMetaData[questionIndex];
    if (!quesMeta) return;

    // 2. Replace at specific alternate (main = 0, first alternate = 1, ...)
    if (typeof altIdx === 'number') {
      const sortedAlternates = [...(quesMeta.alternateQuestions ?? [])].sort(
        (a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0)
      );
      const allQuestions = [
        new SectionQuestionMetaData({
          questionId: quesMeta.questionId,
          sequence: quesMeta.sequence,
          showMedia: quesMeta.showMedia,
          isAiGenerated: quesMeta.isAiGenerated,
          maxMarks: quesMeta.maxMarks ?? 0,
          questionType: quesMeta.questionType,
          alternateQuestions: [],
        }),
        ...sortedAlternates.map(
          (val) =>
            new SectionQuestionMetaData({
              questionId: val.questionId,
              sequence: val.sequence,
              showMedia: val.showMedia,
              isAiGenerated: val.isAiGenerated,
              maxMarks: val.maxMarks ?? 0,
              questionType: val.questionType,
              alternateQuestions: [],
            })
        ),
      ];
      const [removed] = allQuestions.splice(altIdx, 1);
      const deletedQuestionId = removed.questionId;
      const questionType = question.questionType;
      const subSectionQuestionTypeMarks = subSection.questionTypeAndMarks;
      const questionTypeMarks = subSectionQuestionTypeMarks.find(
        (val) => val.questionType === questionType
      );
      if (
        questionTypeMarks &&
        question?.question?.model?.value?.commonQuestionContent
      ) {
        question.question.model.value.commonQuestionContent.marks[0] =
          questionTypeMarks.marks;
      }
      removed.questionId = question.questionId || '';
      removed.maxMarks =
        question?.question?.model?.value?.commonQuestionContent?.marks[0] ?? 0;
      if (question.isAiGenerated !== undefined)
        removed.isAiGenerated = question.isAiGenerated;
      allQuestions.splice(altIdx, 0, removed);
      const newSectionQuestionMetaData = new SectionQuestionMetaData({
        questionId: allQuestions[0].questionId,
        sequence: quesMeta.sequence,
        showMedia: allQuestions[0].showMedia,
        isAiGenerated: allQuestions[0].isAiGenerated,
        maxMarks: allQuestions[0].maxMarks ?? 0,
        questionType: allQuestions[0].questionType,
        alternateQuestions: allQuestions.slice(1).map(
          (val, index) =>
            new SectionQuestionMetaData({
              questionId: val.questionId,
              sequence: index + 1,
              showMedia: val.showMedia,
              isAiGenerated: val.isAiGenerated,
              maxMarks: val.maxMarks ?? 0,
              questionType: val.questionType,
              alternateQuestions: [],
            })
        ),
      });
      subSection.subSectionMetaData[questionIndex] = newSectionQuestionMetaData;
      section.multiSubjectSubSections[subSectionIndex] = subSection;
      const updatedContent = cloneObject(fa_content);
      updatedContent.multiSubjectSections[sectionIndex] = section;
      const checkIfDeletedQuesExistInOtherSections =
        updatedContent.multiSubjectSections?.some((sec, secIdx) =>
          sec.multiSubjectSubSections?.some((subSec, subSecIdx) =>
            subSec.subSectionMetaData?.some(
              (ques, qIdx) =>
                (secIdx !== sectionIndex ||
                  subSecIdx !== subSectionIndex ||
                  qIdx !== questionIndex) &&
                (ques.questionId === deletedQuestionId ||
                  (ques.alternateQuestions ?? []).some(
                    (alt) => alt.questionId === deletedQuestionId
                  ))
            )
          )
        );
      if (checkIfDeletedQuesExistInOtherSections) {
        updatedContent.assessmentContent =
          updatedContent.assessmentContent.filter(
            (ques) => ques.questionId !== deletedQuestionId
          );
        if (updatedContent.questionsSequenceInfo) {
          updatedContent.questionsSequenceInfo.questionModuleMap =
            updatedContent.questionsSequenceInfo.questionModuleMap?.filter(
              (val) => val.questionId !== deletedQuestionId
            ) ?? [];
        }
      }
      const ifNewQuestionExistInOtherSection =
        updatedContent.multiSubjectSections?.some((sec, secIdx) =>
          sec.multiSubjectSubSections?.some((subSec, subSecIdx) =>
            subSec.subSectionMetaData?.some(
              (ques, qIdx) =>
                (secIdx !== sectionIndex ||
                  subSecIdx !== subSectionIndex ||
                  qIdx !== questionIndex) &&
                (ques.questionId === question.questionId ||
                  (ques.alternateQuestions ?? []).some(
                    (alt) => alt.questionId === question.questionId
                  ))
            )
          )
        );
      if (!ifNewQuestionExistInOtherSection) {
        updatedContent.assessmentContent = [...assessmentContent, question];
        if (updatedContent.questionsSequenceInfo) {
          updatedContent.questionsSequenceInfo.questionModuleMap = [
            ...(updatedContent.questionsSequenceInfo.questionModuleMap ?? []),
            ...(moduleMapArray?.filter(
              (val) => val.questionId === question.questionId
            ) ?? []),
          ];
        }
      }
      dispatch(setFAContent(updatedContent || undefined));
      return;
    }

    // 3. Add as alternate
    const sequences =
      quesMeta.alternateQuestions?.map((val) => val.sequence ?? 0) ?? [];
    const maxSequence = sequences.length > 0 ? Math.max(...sequences) : 0;
    const questionType = question.questionType;
    const subSectionQuestionTypeMarks = subSection.questionTypeAndMarks;
    const questionTypeMarks = subSectionQuestionTypeMarks.find(
      (val) => val.questionType === questionType
    );
    if (
      questionTypeMarks &&
      question?.question?.model?.value?.commonQuestionContent
    ) {
      question.question.model.value.commonQuestionContent.marks[0] =
        questionTypeMarks.marks;
    }
    quesMeta.alternateQuestions = quesMeta.alternateQuestions ?? [];
    quesMeta.alternateQuestions.push(
      new SectionQuestionMetaData({
        questionId: question.questionId || '',
        sequence: maxSequence + 1,
        showMedia: true,
        isAiGenerated: question.isAiGenerated,
        maxMarks:
          question?.question?.model?.value?.commonQuestionContent?.marks[0] ??
          0,
      })
    );
    subSection.subSectionMetaData[questionIndex] = quesMeta;
    section.multiSubjectSubSections[subSectionIndex] = subSection;
    const updatedContent = cloneObject(fa_content);
    updatedContent.multiSubjectSections[sectionIndex] = section;
    const ifNewQuestionExistInOtherSection =
      updatedContent.multiSubjectSections?.some((sec, secIdx) =>
        sec.multiSubjectSubSections?.some((subSec, subSecIdx) =>
          subSec.subSectionMetaData?.some(
            (ques, qIdx) =>
              (secIdx !== sectionIndex ||
                subSecIdx !== subSectionIndex ||
                qIdx !== questionIndex) &&
              (ques.questionId === question.questionId ||
                (ques.alternateQuestions ?? []).some(
                  (alt) => alt.questionId === question.questionId
                ))
          )
        )
      );
    if (!ifNewQuestionExistInOtherSection) {
      updatedContent.assessmentContent = [...assessmentContent, question];
      if (updatedContent.questionsSequenceInfo) {
        updatedContent.questionsSequenceInfo.questionModuleMap = [
          ...(updatedContent.questionsSequenceInfo.questionModuleMap ?? []),
          ...(moduleMapArray?.filter(
            (val) => val.questionId === question.questionId
          ) ?? []),
        ];
      }
    }
    dispatch(setFAContent(updatedContent || undefined));
  };
};

// =============================================================

export const useDeleteFAQuestionMultiSubject = () => {
  const dispatch = useAppDispatch();
  const { fa_content, fa_selected_section_number } = deserify(
    useAppSelector((state) => state.assessment)
  );

  return (props: {
    subSectionIndex: number;
    questionIndex: number;
    altIdx?: number;
  }) => {
    const { subSectionIndex, questionIndex, altIdx } = props;

    const sectionIndex =
      fa_content?.multiSubjectSections?.findIndex(
        (s) => s.sectionNo === fa_selected_section_number
      ) ?? -1;
    if (sectionIndex < 0) return;

    const section = fa_content?.multiSubjectSections[sectionIndex];
    if (!section) return;
    const subSection = section.multiSubjectSubSections[subSectionIndex];
    if (!subSection) return;
    const quesMeta = subSection.subSectionMetaData[questionIndex];
    if (!quesMeta) return;

    const sortedAlternates = [...(quesMeta.alternateQuestions ?? [])].sort(
      (a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0)
    );
    const allQuestions = [
      new SectionQuestionMetaData({
        questionId: quesMeta.questionId,
        sequence: quesMeta.sequence,
        showMedia: quesMeta.showMedia,
        isAiGenerated: quesMeta.isAiGenerated,
        maxMarks: quesMeta.maxMarks ?? 0,
        questionType: quesMeta.questionType,
        alternateQuestions: [],
      }),
      ...sortedAlternates.map(
        (val) =>
          new SectionQuestionMetaData({
            questionId: val.questionId,
            sequence: val.sequence,
            showMedia: val.showMedia,
            isAiGenerated: val.isAiGenerated,
            maxMarks: val.maxMarks ?? 0,
            questionType: val.questionType,
            alternateQuestions: [],
          })
      ),
    ];

    let removed: SectionQuestionMetaData[] = [];
    if (typeof altIdx === 'number') {
      removed = allQuestions.splice(altIdx, 1);
    } else {
      removed = allQuestions.splice(0, allQuestions.length);
    }

    const removedQuestionIds = removed.map((r) => r.questionId);

    if (allQuestions.length > 0) {
      const newSectionQuestionMetaData = new SectionQuestionMetaData({
        questionId: allQuestions[0].questionId,
        sequence: quesMeta.sequence,
        showMedia: allQuestions[0].showMedia,
        isAiGenerated: allQuestions[0].isAiGenerated,
        maxMarks: allQuestions[0].maxMarks ?? 0,
        questionType: allQuestions[0].questionType,
        alternateQuestions: allQuestions.slice(1).map(
          (val, index) =>
            new SectionQuestionMetaData({
              questionId: val.questionId,
              sequence: index + 1,
              showMedia: val.showMedia,
              isAiGenerated: val.isAiGenerated,
              maxMarks: val.maxMarks ?? 0,
              questionType: val.questionType,
              alternateQuestions: [],
            })
        ),
      });
      subSection.subSectionMetaData[questionIndex] = newSectionQuestionMetaData;
    } else {
      subSection.subSectionMetaData.splice(questionIndex, 1);
    }
    section.multiSubjectSubSections[subSectionIndex] = subSection;
    const updatedContent = cloneObject(fa_content);
    updatedContent.multiSubjectSections[sectionIndex] = section;

    for (const deletedId of removedQuestionIds) {
      const existsElsewhere = updatedContent.multiSubjectSections?.some(
        (sec, secIdx) =>
          sec.multiSubjectSubSections?.some((subSec, subSecIdx) =>
            subSec.subSectionMetaData?.some(
              (ques, qIdx) =>
                (secIdx !== sectionIndex ||
                  subSecIdx !== subSectionIndex ||
                  qIdx !== questionIndex) &&
                (ques.questionId === deletedId ||
                  (ques.alternateQuestions ?? []).some(
                    (alt) => alt.questionId === deletedId
                  ))
            )
          )
      );
      if (!existsElsewhere) {
        updatedContent.assessmentContent =
          updatedContent.assessmentContent.filter(
            (q) => q.questionId !== deletedId
          );
        if (updatedContent.questionsSequenceInfo) {
          updatedContent.questionsSequenceInfo.questionModuleMap =
            updatedContent.questionsSequenceInfo.questionModuleMap?.filter(
              (val) => val.questionId !== deletedId
            ) ?? [];
        }
      }
    }

    if (updatedContent.questionModuleMap?.length) {
      const idsStillUsed = new Set<string>();
      updatedContent.multiSubjectSections?.forEach((sec) =>
        sec.multiSubjectSubSections?.forEach((subSec) =>
          subSec.subSectionMetaData?.forEach((ques) => {
            idsStillUsed.add(ques.questionId || '');
            (ques.alternateQuestions ?? []).forEach((alt) =>
              idsStillUsed.add(alt.questionId || '')
            );
          })
        )
      );
      if (updatedContent.questionsSequenceInfo) {
        updatedContent.questionsSequenceInfo.questionModuleMap =
          updatedContent.questionsSequenceInfo.questionModuleMap?.filter((m) =>
            idsStillUsed.has(m.questionId || '')
          ) ?? [];
      }
    }

    dispatch(setFAContent(updatedContent || undefined));
  };
};

// =============================================================

export const useShowMediaClickFAMultiSubject = () => {
  const { fa_content, fa_selected_section_number } = deserify(
    useAppSelector((state) => state.assessment)
  );
  const dispatch = useAppDispatch();

  return (props: {
    subSectionIndex: number;
    questionIndex: number;
    altIdx: number;
  }) => {
    const { subSectionIndex, questionIndex, altIdx } = props;

    const sectionIndex =
      fa_content?.multiSubjectSections?.findIndex(
        (s) => s.sectionNo === fa_selected_section_number
      ) ?? -1;
    if (sectionIndex < 0) return;

    const section = fa_content?.multiSubjectSections[sectionIndex];
    if (!section) return;
    const subSection = section.multiSubjectSubSections[subSectionIndex];
    if (!subSection) return;
    const quesMeta = subSection.subSectionMetaData[questionIndex];
    if (!quesMeta) return;

    const sortedAlternates = [...(quesMeta.alternateQuestions ?? [])].sort(
      (a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0)
    );
    const allQuestions = [
      new SectionQuestionMetaData({
        questionId: quesMeta.questionId,
        sequence: quesMeta.sequence,
        showMedia: quesMeta.showMedia,
        isAiGenerated: quesMeta.isAiGenerated,
        maxMarks: quesMeta.maxMarks ?? 0,
        questionType: quesMeta.questionType,
        alternateQuestions: [],
      }),
      ...sortedAlternates.map(
        (val) =>
          new SectionQuestionMetaData({
            questionId: val.questionId,
            sequence: val.sequence,
            showMedia: val.showMedia,
            isAiGenerated: val.isAiGenerated,
            maxMarks: val.maxMarks ?? 0,
            questionType: val.questionType,
            alternateQuestions: [],
          })
      ),
    ];
    const target = allQuestions[altIdx];
    if (target) target.showMedia = !target.showMedia;

    const newSectionQuestionMetaData = new SectionQuestionMetaData({
      questionId: allQuestions[0].questionId,
      sequence: quesMeta.sequence,
      showMedia: allQuestions[0].showMedia,
      isAiGenerated: allQuestions[0].isAiGenerated,
      maxMarks: allQuestions[0].maxMarks ?? 0,
      questionType: allQuestions[0].questionType,
      alternateQuestions: allQuestions.slice(1).map(
        (val, index) =>
          new SectionQuestionMetaData({
            questionId: val.questionId,
            sequence: index + 1,
            showMedia: val.showMedia,
            isAiGenerated: val.isAiGenerated,
            maxMarks: val.maxMarks ?? 0,
            questionType: val.questionType,
            alternateQuestions: [],
          })
      ),
    });
    subSection.subSectionMetaData[questionIndex] = newSectionQuestionMetaData;
    section.multiSubjectSubSections[subSectionIndex] = subSection;
    const updatedContent = cloneObject(fa_content);
    updatedContent.multiSubjectSections[sectionIndex] = section;
    dispatch(setFAContent(updatedContent || undefined));
  };
};

// =============================================================

/**
 * Builds a single FA section from assessmentContent questions.
 * Optionally excludes a set of question IDs (used while importing from library).
 */
export const getSectionFromAssessmentContent = (props: {
  assessData: AssessmentContent;
  subjectSectionNo?: number;
  subjectCollection?: TeachClassSubjects[];
  questionIdsToExclude?: string[];
  marking_scheme?: MarkingSchemeEnum;
}) => {
  const {
    assessData,
    subjectSectionNo,
    subjectCollection,
    questionIdsToExclude,
    marking_scheme,
  } = props;

  const questionIdsToExcludeSet = new Set(questionIdsToExclude ?? []);
  const questions = (assessData.assessmentContent ?? []).filter(
    (q) => !questionIdsToExcludeSet.has(q.questionId ?? '')
  );

  const faSubjectSubSection: MultiSubjectSubSection[] = [
    new MultiSubjectSubSection({
      subSectionName: 'subSection 1 A',
      subSectionTitle: 'subSection 1 A',
      subSectionNo: 1,
      subSectionMetaData: questions.map(
        (q, index) =>
          new SectionQuestionMetaData({
            questionId: q.questionId, // adjust based on your Question model
            alternateQuestions: [], // empty array
            sequence: index + 1, // incremental sequence
            showMedia: true,
            isAiGenerated: false, // optional if needed
            maxMarks:
              marking_scheme === MarkingSchemeEnum.MARKING_SCHEME_JEE
                ? 4
                : q.question?.model?.value?.commonQuestionContent?.marks[0], // as per requirement
            questionType: q.questionType, // optional if available
          })
      ),
      // questionTypeAndMarks: questionMarksMappingForQP,
      questionTypeAndMarks: [],
    }),
  ];

  const multiSubjectSubSections = faSubjectSubSection;

  const totalMarks = questions.reduce((sum, q) => {
    const questionCommonContentModel =
      q?.question?.model?.value?.commonQuestionContent;
    const maxMarks =
      questionCommonContentModel?.marks?.reduce((x, y) => x + y, 0) || 1;
    return sum + maxMarks;
  }, 0);

  const difficultyLevels = questions.map(
    (qs) => qs.questionMeta?.difficultyLevel
  ) as DifficultyLevelEnum[];

  const avgDifficultyLevel = calculateAverageDifficulty(difficultyLevels);

  return new MultiSubjectSection({
    subjectId: assessData.assessment?.subjectId,
    subject:
      getTeacherSubjectEnum(
        assessData.assessment?.subjectId,
        subjectCollection
      ) ?? Subject_SubjectEnum.UNDEFINED,
    sectionNo: subjectSectionNo,
    multiSubjectSectionStatus:
      MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_APPROVED,
    assessmentSectionAttemptStatus:
      TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_PENDING,
    sectionName: '',
    sectionTitle: '',
    totalMarks: totalMarks,
    numberOfQuestion: questions?.length ?? 0,
    avgDifficultyLevel: avgDifficultyLevel,
    multiSubjectSubSections: multiSubjectSubSections,
    chapterInfo: new ChapterInfo({
      chapterTopicsInfo:
        assessData?.assessment?.taskMetaInfo?.chapterInfo?.chapterTopicsInfo,
    }),
    markingSchemeType: marking_scheme,
  });
};

// =============================================================

/**
 * Collects unique question IDs (including alternates) from one section.
 */
export const getQuestionIdsFromMultiSubjectSection = (
  section?: MultiSubjectSection
): string[] => {
  if (!section?.multiSubjectSubSections?.length) return [];

  const ids = new Set<string>();
  for (const subSection of section.multiSubjectSubSections ?? []) {
    for (const meta of subSection.subSectionMetaData ?? []) {
      if (meta.questionId) ids.add(meta.questionId);
      for (const alt of meta.alternateQuestions ?? []) {
        if (alt.questionId) ids.add(alt.questionId);
      }
    }
  }

  return Array.from(ids);
};

// =============================================================

/**
 * Returns overlapping question IDs between current FA (excluding the target section)
 * and a selected library section.
 */
export const getRepeatingQuestionIdsForLibrarySection = (props: {
  faContent: AssessmentContent | undefined;
  sectionNumberToReplace?: number;
  librarySection?: MultiSubjectSection;
}): string[] => {
  const { faContent, sectionNumberToReplace, librarySection } = props;
  if (!librarySection) return [];

  const existingQuestionIds = new Set(
    getUniqueQuestionIdsFromFAContent({
      faContent,
      sectionNumbersToIgnore:
        sectionNumberToReplace != null ? [sectionNumberToReplace] : undefined,
    })
  );
  if (existingQuestionIds.size === 0) return [];

  return getQuestionIdsFromMultiSubjectSection(librarySection).filter((id) =>
    existingQuestionIds.has(id)
  );
};

// =============================================================

/**
 * Returns overlapping question IDs between current FA (excluding the target section)
 * and a selected library assessment's flat assessmentContent list.
 */
export const getRepeatingQuestionIdsForLibraryAssessment = (props: {
  faContent: AssessmentContent | undefined;
  sectionNumberToReplace?: number;
  libraryAssessmentContent: AssessmentContent;
}): string[] => {
  const { faContent, sectionNumberToReplace, libraryAssessmentContent } = props;

  const existingQuestionIds = new Set(
    getUniqueQuestionIdsFromFAContent({
      faContent,
      sectionNumbersToIgnore:
        sectionNumberToReplace != null ? [sectionNumberToReplace] : undefined,
    })
  );
  if (existingQuestionIds.size === 0) return [];

  return (libraryAssessmentContent.assessmentContent ?? [])
    .map((q) => q.questionId)
    .filter((id): id is string => !!id)
    .filter((id) => existingQuestionIds.has(id));
};

// =============================================================

/**
 * Removes question IDs from a section while preserving subsection/question shape,
 * then recomputes summary counters like numberOfQuestion and totalMarks.
 */
export const getSectionWithFilteredQuestionIds = (props: {
  section: MultiSubjectSection;
  questionIdsToRemove: string[];
  markingScheme?: MarkingSchemeEnum;
}): MultiSubjectSection => {
  const { section, questionIdsToRemove, markingScheme } = props;
  if (!questionIdsToRemove.length) return new MultiSubjectSection(section);

  const removeSet = new Set(questionIdsToRemove);
  const clonedSection = cloneObject(section);

  clonedSection.multiSubjectSubSections = (
    clonedSection.multiSubjectSubSections ?? []
  ).map((subSection) => {
    const filteredMetaData = (subSection.subSectionMetaData ?? []).reduce(
      (acc: SectionQuestionMetaData[], meta) => {
        const sortedAlternates = [...(meta.alternateQuestions ?? [])].sort(
          (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
        );
        const allQuestions = [
          new SectionQuestionMetaData({
            questionId: meta.questionId,
            sequence: meta.sequence,
            showMedia: meta.showMedia,
            isAiGenerated: meta.isAiGenerated,
            maxMarks:
              markingScheme === MarkingSchemeEnum.MARKING_SCHEME_JEE
                ? 4
                : meta.maxMarks ?? 0,
            questionType: meta.questionType,
            alternateQuestions: [],
          }),
          ...sortedAlternates.map(
            (alt) =>
              new SectionQuestionMetaData({
                questionId: alt.questionId,
                sequence: alt.sequence,
                showMedia: alt.showMedia,
                isAiGenerated: alt.isAiGenerated,
                maxMarks:
                  markingScheme === MarkingSchemeEnum.MARKING_SCHEME_JEE
                    ? 4
                    : alt.maxMarks ?? 0,
                questionType: alt.questionType,
                alternateQuestions: [],
              })
          ),
        ].filter((item) => !removeSet.has(item.questionId));

        if (!allQuestions.length) return acc;

        acc.push(
          new SectionQuestionMetaData({
            ...meta,
            questionId: allQuestions[0].questionId,
            alternateQuestions: allQuestions.slice(1).map(
              (item, index) =>
                new SectionQuestionMetaData({
                  ...item,
                  sequence: index + 1,
                  alternateQuestions: [],
                })
            ),
          })
        );
        return acc;
      },
      []
    );

    return new MultiSubjectSubSection({
      ...subSection,
      subSectionMetaData: filteredMetaData.map(
        (meta, index) =>
          new SectionQuestionMetaData({
            ...meta,
            sequence: index + 1,
          })
      ),
    });
  });

  clonedSection.numberOfQuestion = clonedSection.multiSubjectSubSections.reduce(
    (sum, subSection) => sum + (subSection.subSectionMetaData?.length ?? 0),
    0
  );
  clonedSection.totalMarks = clonedSection.multiSubjectSubSections.reduce(
    (sum, subSection) =>
      sum +
      (subSection.subSectionMetaData ?? []).reduce(
        (subSum, meta) =>
          subSum +
          (meta.maxMarks ?? 0) +
          (meta.alternateQuestions ?? []).reduce(
            (altSum, alt) => altSum + (alt.maxMarks ?? 0),
            0
          ),
        0
      ),
    0
  );

  return new MultiSubjectSection(clonedSection);
};

// =============================================================

/**
 * For `createAssessment: true` we want to send to backend only the chapters that
 * actually contribute questions in the currently selected section.
 *
 * - Filters `chapterTopicsInfo` to chapters hit by selected (main) questions.
 * - Updates each chapter's `noOfQuestions` based on how many selected questions
 *   map to that chapter via `questionModuleMap`.
 */
export const getChapterInfoFromSelectedSectionQuestions = (props: {
  section?: MultiSubjectSection;
  questionModuleMap?: QuestionModuleMap[];
  chapterTopicsInfo?: ChapterIdsInfo[];
}): ChapterInfo => {
  const { section, questionModuleMap, chapterTopicsInfo } = props;

  // If we don't have enough data to recompute, preserve whatever is already set.
  if (!section || !chapterTopicsInfo?.length) {
    return section?.chapterInfo ?? new ChapterInfo({ chapterTopicsInfo: [] });
  }

  const selectedQuestionIds = new Set(
    (section.multiSubjectSubSections ?? [])
      .flatMap((subSection) => subSection.subSectionMetaData ?? [])
      .map((meta) => meta.questionId ?? '')
      .filter((id): id is string => !!id)
  );

  const moduleMap = questionModuleMap ?? [];

  // topicId -> chapterId (used when module map only provides topicId)
  const topicIdToChapterId = new Map<number, number>();
  for (const ch of chapterTopicsInfo ?? []) {
    for (const t of ch.topicInfo ?? []) {
      if (typeof t.topicId !== 'number') continue;
      if (!topicIdToChapterId.has(t.topicId)) {
        topicIdToChapterId.set(t.topicId, ch.chapterId);
      }
    }
  }

  const chapterIdToCount = new Map<number, number>();
  const topicIdToCount = new Map<number, number>();

  for (const mapItem of moduleMap) {
    if (!selectedQuestionIds.has(mapItem.questionId ?? '')) continue;

    if (typeof mapItem.chapterId === 'number') {
      chapterIdToCount.set(
        mapItem.chapterId,
        (chapterIdToCount.get(mapItem.chapterId) ?? 0) + 1
      );
    } else if (typeof mapItem.topicId === 'number') {
      const chapterId = topicIdToChapterId.get(mapItem.topicId);
      if (typeof chapterId === 'number') {
        chapterIdToCount.set(
          chapterId,
          (chapterIdToCount.get(chapterId) ?? 0) + 1
        );
      }
    }

    if (typeof mapItem.topicId === 'number') {
      topicIdToCount.set(
        mapItem.topicId,
        (topicIdToCount.get(mapItem.topicId) ?? 0) + 1
      );
    }
  }

  // If mapping failed (no selected question matched any chapter), keep original.
  if (chapterIdToCount.size === 0) {
    return section.chapterInfo ?? new ChapterInfo({ chapterTopicsInfo: [] });
  }

  const filteredChapterTopicsInfo = (chapterTopicsInfo ?? []).filter((ch) =>
    chapterIdToCount.has(ch.chapterId)
  );

  if (filteredChapterTopicsInfo.length === 0) {
    return section.chapterInfo ?? new ChapterInfo({ chapterTopicsInfo: [] });
  }

  return new ChapterInfo({
    chapterTopicsInfo: filteredChapterTopicsInfo.map((ch) => {
      const noOfQuestions = chapterIdToCount.get(ch.chapterId) ?? 0;
      const topicInfo = (ch.topicInfo ?? []).map((topic) => ({
        ...topic,
        noOfQuestions: topicIdToCount.get(topic.topicId ?? -1) ?? 0,
      }));

      return new ChapterIdsInfo({
        chapterId: ch.chapterId,
        name: ch.name,
        chapterNo: ch.chapterNo,
        noOfQuestions,
        subjectId: ch.subjectId,
        topicInfo,
      });
    }),
  });
};

// =============================================================

/**
 * Filters `questionModuleMap` to only keep entries for questions that exist
 * inside the provided `metaInfo` sections.
 *
 * A question is considered present if it appears either as:
 * - the main `subSectionMetaData[].questionId`, or
 * - inside any `alternateQuestions[].questionId`.
 */
export const getLatestQuestionModuleMap = (props: {
  metaInfo?: MultiSubjectSection[];
  questionModuleMap?: QuestionModuleMap[];
}): QuestionModuleMap[] => {
  const { metaInfo, questionModuleMap } = props;

  const moduleMap = questionModuleMap ?? [];
  const sections = metaInfo ?? [];

  if (!sections.length) return [];

  const presentQuestionIds = new Set<string>();
  const orderedQuestionIds: string[] = [];

  for (const section of sections) {
    for (const subSection of section.multiSubjectSubSections ?? []) {
      for (const meta of subSection.subSectionMetaData ?? []) {
        if (meta.questionId && !presentQuestionIds.has(meta.questionId)) {
          presentQuestionIds.add(meta.questionId);
          orderedQuestionIds.push(meta.questionId);
        }
        for (const alt of meta.alternateQuestions ?? []) {
          if (alt.questionId && !presentQuestionIds.has(alt.questionId)) {
            presentQuestionIds.add(alt.questionId);
            orderedQuestionIds.push(alt.questionId);
          }
        }
      }
    }
  }

  // Ensure output contains a module-map entry for every questionId found in metaInfo.
  // If `questionModuleMap` doesn't have the entry, we still include it with only `questionId`.
  return orderedQuestionIds.map(
    (qId) =>
      moduleMap.find((m) => m.questionId === qId) ??
      new QuestionModuleMap({ questionId: qId })
  );
};

// =============================================================
/**
 * Normalizes question marks + section totals for JEE marking scheme.
 *
 * Requirement:
 * - When marking scheme is JEE, each question's `commonQuestionContent.marks`
 *   should be `4`.
 * - Also ensures `subSectionMetaData[].maxMarks` and section `totalMarks` /
 *   `numberOfQuestion` remain consistent with the updated marks.
 */
export const applyJeeMarksToAllQuestions = (props: {
  assessmentData: AssessmentContent;
}) => {
  const { assessmentData } = props;
  const updated = cloneObject(assessmentData);

  const JEEMarks = 4;

  // 1) Update question model-level marks.
  for (const q of updated.assessmentContent ?? []) {
    const commonQuestionContent =
      q?.question?.model?.value?.commonQuestionContent;
    if (!commonQuestionContent) continue;

    const marks = commonQuestionContent.marks;
    if (Array.isArray(marks) && marks.length > 0) {
      // Keep the total marks of the question equal to `4`.
      // Existing code elsewhere updates `marks[0]`, so we follow that convention.
      marks[0] = JEEMarks;
      for (let i = 1; i < marks.length; i++) marks[i] = 0;
    } else {
      commonQuestionContent.marks = [JEEMarks];
    }
  }

  // 2) Update section meta-level maxMarks + recompute totals.
  for (const section of updated.multiSubjectSections ?? []) {
    for (const subSection of section.multiSubjectSubSections ?? []) {
      for (const meta of subSection.subSectionMetaData ?? []) {
        meta.maxMarks = JEEMarks;
        for (const alt of meta.alternateQuestions ?? [])
          alt.maxMarks = JEEMarks;
      }
    }

    section.numberOfQuestion = (section.multiSubjectSubSections ?? []).reduce(
      (sum, subSection) => sum + (subSection.subSectionMetaData?.length ?? 0),
      0
    );

    section.totalMarks = (section.multiSubjectSubSections ?? []).reduce(
      (sum, subSection) => {
        const subTotal = (subSection.subSectionMetaData ?? []).reduce(
          (subSum, meta) => {
            const altTotal = (meta.alternateQuestions ?? []).reduce(
              (altSum, alt) => altSum + (alt.maxMarks ?? 0),
              0
            );
            return subSum + (meta.maxMarks ?? 0) + altTotal;
          },
          0
        );
        return sum + subTotal;
      },
      0
    );
  }

  return updated;
};

// =============================================================

/**
 * Recomputes chapter/topic `noOfQuestions` for one section by using that section's
 * selected question IDs and the provided `questionModuleMap`.
 *
 * Intended usage:
 * - after replacing one FA section from library content
 * - pass `fetchedAssessmentContent.questionModuleMap` from the library payload
 */
export const getUpdatedChapterInfo = (props: {
  assessmentData: AssessmentContent;
  sectionNumber?: number;
  questionModuleMap?: QuestionModuleMap[];
}): AssessmentContent => {
  const { assessmentData, sectionNumber, questionModuleMap } = props;
  if (sectionNumber == null) return assessmentData;

  const updatedContent = cloneObject(assessmentData);
  const targetSection = updatedContent.multiSubjectSections?.find(
    (section) => section.sectionNo === sectionNumber
  );
  if (!targetSection?.chapterInfo?.chapterTopicsInfo?.length)
    return updatedContent;

  const selectedQuestionIds = new Set<string>(
    (targetSection.multiSubjectSubSections ?? [])
      .flatMap((subSection) => subSection.subSectionMetaData ?? [])
      .map((meta) => meta.questionId ?? '')
      .filter((id): id is string => !!id)
  );

  if (!selectedQuestionIds.size) return updatedContent;

  const chapterCounts = new Map<number, number>();
  const topicCounts = new Map<number, number>();

  for (const mapItem of questionModuleMap ?? []) {
    const questionId = mapItem.questionId ?? '';
    if (!selectedQuestionIds.has(questionId)) continue;

    if (typeof mapItem.chapterId === 'number') {
      chapterCounts.set(
        mapItem.chapterId,
        (chapterCounts.get(mapItem.chapterId) ?? 0) + 1
      );
    }
    if (typeof mapItem.topicId === 'number') {
      topicCounts.set(
        mapItem.topicId,
        (topicCounts.get(mapItem.topicId) ?? 0) + 1
      );
    }
  }

  const updatedChapters = (
    targetSection.chapterInfo.chapterTopicsInfo ?? []
  ).map((chapter) => {
    const updatedTopics = (chapter.topicInfo ?? []).map((topic) => ({
      ...topic,
      noOfQuestions: topicCounts.get(topic.topicId ?? -1) ?? 0,
    }));

    return new ChapterIdsInfo({
      ...chapter,
      noOfQuestions: chapterCounts.get(chapter.chapterId) ?? 0,
      topicInfo: updatedTopics,
    });
  });

  targetSection.chapterInfo = new ChapterInfo({
    chapterTopicsInfo: updatedChapters.filter((chapter) => {
      const chapterQuestionCount = chapter.noOfQuestions ?? 0;
      const allTopicsZero = (chapter.topicInfo ?? []).every(
        (topic) => (topic.noOfQuestions ?? 0) === 0
      );
      return !(chapterQuestionCount === 0 && allTopicsZero);
    }),
  });

  return updatedContent;
};

// =============================================================

/**
 * Recomputes chapter/topic `noOfQuestions` for a single section by using
 * selected question IDs in that section and the provided `questionModuleMap`.
 *
 * Also removes chapters where:
 * - chapter `noOfQuestions` is 0, and
 * - every topic inside that chapter has `noOfQuestions` as 0.
 */
export const getUpdatedSectionChapterInfoFromQuestionModuleMap = (props: {
  section?: MultiSubjectSection;
  questionModuleMap?: QuestionModuleMap[];
}): ChapterInfo => {
  const { section, questionModuleMap } = props;
  const chapterTopicsInfo = section?.chapterInfo?.chapterTopicsInfo ?? [];

  if (!section || !chapterTopicsInfo.length) {
    return section?.chapterInfo ?? new ChapterInfo({ chapterTopicsInfo: [] });
  }

  const selectedQuestionIds = new Set<string>();
  for (const subSection of section.multiSubjectSubSections ?? []) {
    for (const meta of subSection.subSectionMetaData ?? []) {
      if (meta.questionId) selectedQuestionIds.add(meta.questionId);
      for (const alt of meta.alternateQuestions ?? []) {
        if (alt.questionId) selectedQuestionIds.add(alt.questionId);
      }
    }
  }

  const chapterCounts = new Map<number, number>();
  const topicCounts = new Map<number, number>();

  for (const mapItem of questionModuleMap ?? []) {
    if (!selectedQuestionIds.has(mapItem.questionId ?? '')) continue;

    if (typeof mapItem.chapterId === 'number') {
      chapterCounts.set(
        mapItem.chapterId,
        (chapterCounts.get(mapItem.chapterId) ?? 0) + 1
      );
    }

    if (typeof mapItem.topicId === 'number') {
      topicCounts.set(mapItem.topicId, (topicCounts.get(mapItem.topicId) ?? 0) + 1);
    }
  }

  const updatedChapters = chapterTopicsInfo
    .map((chapter) => {
      const updatedTopics = (chapter.topicInfo ?? []).map((topic) => ({
        ...topic,
        noOfQuestions: topicCounts.get(topic.topicId ?? -1) ?? 0,
      }));

      const chapterNoOfQuestionsFromTopics = updatedTopics.reduce(
        (sum, topic) => sum + (topic.noOfQuestions ?? 0),
        0
      );

      return new ChapterIdsInfo({
        ...chapter,
        noOfQuestions:
          chapterCounts.get(chapter.chapterId) ?? chapterNoOfQuestionsFromTopics,
        topicInfo: updatedTopics,
      });
    })
    .filter((chapter) => {
      const chapterQuestionCount = chapter.noOfQuestions ?? 0;
      const allTopicsZero = (chapter.topicInfo ?? []).every(
        (topic) => (topic.noOfQuestions ?? 0) === 0
      );
      return !(chapterQuestionCount === 0 && allTopicsZero);
    });

  return new ChapterInfo({ chapterTopicsInfo: updatedChapters });
};

// =============================================================
